import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Award, Clock, Download, ExternalLink, FilterX, RefreshCw, Send, UserCheck, UserX } from "lucide-react";
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AppButton, AppDataTable, Callout, ConfirmModal,
  FilterToolbar, FormField, Input, ProgressBar, StatsCard, StatsGrid, StatusBadge, Textarea, applyColumnFilters,
  isColumnFilterActive, sortRows,
} from "../../ui";
import apiClient from "../../../utils/apiClient";

const formatMinutes = (seconds) => {
  const m = Math.round((Number(seconds) || 0) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
};

/** The one word a row is filed under — also what the Status filter offers. */
const statusOf = (row) => {
  if (!row.attended) return "absent";
  return row.eligible ? "eligible" : "short";
};

const STATUS = {
  eligible: { tone: "success", label: "Eligible" },
  short: { tone: "warning", label: "Below the bar" },
  absent: { tone: "neutral", label: "Didn’t join" },
};

const CERT_OPTIONS = [
  { value: "issued", label: "Issued" },
  { value: "revoked", label: "Revoked" },
  { value: "none", label: "Not issued" },
];

/**
 * AttendancePanel — who attended how much of the session, who earned a
 * certificate, and the actions an admin needs when the rule was not enough:
 * issue anyway, correct a name and reissue, resend, revoke.
 *
 * Loaded whole (the endpoint caps it), so the column filters and sort work in
 * the browser and are honest about every row.
 *
 * `canManage` is false for instructors: they may look, but issuing and
 * revoking certificates is an institute admin's call (the backend refuses too).
 */
const AttendancePanel = ({ apiBase, sessionId, sessionStatus, noun = "webinar", canManage = true, notify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: "percent", direction: "desc" });
  const [filters, setFilters] = useState({});
  const [dialog, setDialog] = useState(null); // { type, row, name?, reason? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await apiClient.get(`${apiBase}/sessions/${sessionId}/attendance`);
      setData(res);
    } catch (err) {
      notify?.(err?.response?.data?.message || "Failed to load attendance.", "error");
    }
    setLoading(false);
  }, [apiBase, sessionId, notify]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const policy = summary?.certificate || {};
  const bar = policy.minAttendancePct || 80;

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (ok) notify?.(typeof ok === "function" ? ok(res?.data) : ok, "success");
      setDialog(null);
      await load();
    } catch (err) {
      notify?.(err?.response?.data?.message || "That did not work. Please try again.", "error");
    }
    setBusy(false);
  };

  const leadUrl = (row, suffix) => `${apiBase}/sessions/${sessionId}/leads/${row._id}/certificate${suffix}`;

  const issue = (row, name) => act(
    () => apiClient.post(leadUrl(row, ""), { name: name || undefined }),
    (res) => (res?.reissued ? `Certificate reissued to ${res.certificate?.participantName}.` : `Certificate issued to ${row.name}.`),
  );
  const resend = (row) => act(
    () => apiClient.post(leadUrl(row, "/resend")),
    (res) => (res?.delivered?.email || res?.delivered?.whatsapp
      ? `Sent again to ${row.name}.`
      : `Nothing could be sent to ${row.name} — check their email and WhatsApp.`),
  );
  const revoke = (row, reason) => act(
    () => apiClient.post(leadUrl(row, "/revoke"), { reason }),
    `Certificate revoked. Its verification page now says so.`,
  );
  const releaseAll = () => act(
    () => apiClient.post(`${apiBase}/sessions/${sessionId}/certificates/issue-eligible`),
    (res) => `Issuing ${res?.queued ?? ""} certificate${res?.queued === 1 ? "" : "s"} — they arrive within a few minutes.`,
  );

  const download = async (row) => {
    try {
      const { data: blob } = await apiClient.get(leadUrl(row, ".pdf"), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certificate-${row.certificate?.certificateNo || row._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      notify?.("Download failed. Please try again.", "error");
    }
  };

  const columns = useMemo(() => [
    {
      key: "name",
      label: "Registrant",
      sortable: true,
      // Reads name, email and mobile, because that is what someone types to find a person.
      filter: { type: "text", placeholder: "Name, email or mobile" },
      filterValue: (r) => [r.name, r.email, r.mobile].filter(Boolean).join(" "),
      render: (r) => (
        <div className="tw-min-w-0">
          <div className="tw-truncate tw-font-medium tw-text-content">{r.name || "—"}</div>
          <div className="tw-truncate tw-text-xs tw-text-content-subtle">
            {r.isGuest ? "Guest (name only)" : [r.email, r.mobile].filter(Boolean).join(" · ")}
          </div>
        </div>
      ),
    },
    {
      key: "attendedSeconds",
      label: "Time in room",
      sortable: true,
      align: "right",
      render: (r) => <span className="tw-tabular-nums">{r.attended ? formatMinutes(r.attendedSeconds) : "—"}</span>,
    },
    {
      key: "percent",
      label: "Attendance",
      sortable: true,
      filterValue: (r) => (r.attended ? r.percent : null),
      render: (r) => (r.attended ? (
        <div className="tw-flex tw-min-w-[9rem] tw-items-center tw-gap-2">
          <ProgressBar value={r.percent} color={r.eligible ? "success" : "warning"} className="tw-flex-1" />
          <span className="tw-w-10 tw-text-right tw-text-xs tw-font-semibold tw-tabular-nums tw-text-content">{r.percent}%</span>
        </div>
      ) : <span className="tw-text-content-subtle">—</span>),
    },
    {
      key: "status",
      label: "Status",
      filter: { type: "set", options: Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label })) },
      filterValue: statusOf,
      render: (r) => {
        const s = STATUS[statusOf(r)];
        return <StatusBadge status={s.tone}>{s.label}</StatusBadge>;
      },
    },
    ...(policy.enabled ? [{
      key: "certificate",
      label: "Certificate",
      filter: { type: "set", options: CERT_OPTIONS },
      filterValue: (r) => r.certificate?.status || "none",
      render: (r) => {
        if (!r.certificate) return <span className="tw-text-content-subtle">—</span>;
        if (r.certificate.status === "revoked") return <StatusBadge status="danger">Revoked</StatusBadge>;
        return (
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-0.5">
            <StatusBadge status="success">{r.certificate.mode === "manual" ? "Issued by admin" : "Issued"}</StatusBadge>
            <span className="tw-font-mono tw-text-[0.6875rem] tw-text-content-subtle">{r.certificate.certificateNo}</span>
          </div>
        );
      },
    }] : []),
    ...(canManage && policy.enabled ? [{
      key: "actions",
      label: "",
      pinned: "right",
      align: "right",
      render: (r) => (
        <ActionMenu label={`Certificate actions for ${r.name}`}>
          {(!r.certificate || r.certificate.status === "revoked") && (
            <ActionMenuItem onClick={() => setDialog({ type: "issue", row: r })}>
              <Award size={16} /> {r.eligible ? "Issue certificate" : "Issue anyway…"}
            </ActionMenuItem>
          )}
          {r.certificate?.status === "issued" && (
            <>
              <ActionMenuItem onClick={() => resend(r)} disabled={busy}><Send size={16} /> Send again</ActionMenuItem>
              <ActionMenuItem onClick={() => download(r)}><Download size={16} /> Download PDF</ActionMenuItem>
              <ActionMenuItem onClick={() => window.open(r.certificate.verifyUrl, "_blank", "noopener")}>
                <ExternalLink size={16} /> Verification page
              </ActionMenuItem>
              <ActionMenuItem onClick={() => setDialog({ type: "rename", row: r, name: r.certificate.participantName })}>
                <RefreshCw size={16} /> Correct name &amp; reissue…
              </ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem danger onClick={() => setDialog({ type: "revoke", row: r, reason: "" })}>Revoke…</ActionMenuItem>
            </>
          )}
        </ActionMenu>
      ),
    }] : []),
  ], [policy.enabled, canManage, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const allRows = data?.rows || [];
  const visible = useMemo(
    () => sortRows(applyColumnFilters(allRows, columns, filters), columns, sort),
    [allRows, columns, filters, sort],
  );
  const filtersOn = Object.values(filters).some(isColumnFilterActive);

  const ended = sessionStatus === "ended";
  const waitingToIssue = policy.enabled && ended && !summary?.finalizedAt && policy.autoSend;
  const heldForReview = policy.enabled && ended && policy.autoSend === false;
  const unissuedEligible = allRows.filter((r) => r.eligible && r.certificate?.status !== "issued").length;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Registered and Joined are already at the top of the page. */}
      {summary && (
        <StatsGrid>
          <StatsCard icon={Clock} iconColor="violet" title="Average attendance" value={`${summary.averagePercent}%`} subtitle={`of ${formatMinutes(summary.baseSeconds)}`} />
          <StatsCard icon={UserCheck} iconColor="emerald" title={`At ${bar}% or more`} value={summary.eligible} subtitle="Eligible for a certificate" />
          <StatsCard
            icon={Award}
            iconColor="amber"
            title="Certificates issued"
            value={policy.enabled ? summary.certificatesIssued : "Off"}
            subtitle={policy.enabled ? null : "Switch on in Edit"}
          />
          <StatsCard icon={UserX} iconColor="rose" title="Didn’t join" value={Math.max(0, summary.registered - summary.attended)} subtitle="Registered, never joined" />
        </StatsGrid>
      )}

      {sessionStatus === "live" && (
        <Callout tone="info">
          Attendance is counting now. Final numbers — and certificates — are settled about 10 minutes after you end the {noun}.
        </Callout>
      )}
      {waitingToIssue && (
        <Callout tone="info">Certificates go out about 10 minutes after the {noun} ended. This page updates when they have.</Callout>
      )}
      {heldForReview && canManage && unissuedEligible > 0 && (
        <Callout
          tone="warning"
          action={(
            <AppButton size="sm" onClick={() => setDialog({ type: "release" })} disabled={busy}>
              <Award size={16} /> Issue {unissuedEligible}
            </AppButton>
          )}
        >
          Certificates are held for your review. {unissuedEligible} attendee{unissuedEligible === 1 ? " has" : "s have"} earned one.
        </Callout>
      )}
      {summary?.truncated && (
        <Callout tone="warning">Showing the most recent 5,000 registrants. Export the CSV for everyone.</Callout>
      )}

      <AppDataTable
        fill={false}
        columns={columns}
        rows={visible}
        rowKey={(r) => r._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={filters}
        onColumnFilterChange={(key, value) => setFilters((f) => ({ ...f, [key]: value }))}
        emptyMessage={`Nobody has registered for this ${noun} yet.`}
        toolbar={(
          <FilterToolbar flush>
            <span className="tw-text-sm tw-text-content-subtle">
              {visible.length === allRows.length ? `${allRows.length} registrants` : `${visible.length} of ${allRows.length}`}
            </span>
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setFilters({})}>
                <FilterX size={16} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        )}
      />

      <ConfirmModal
        show={dialog?.type === "issue"}
        onHide={() => setDialog(null)}
        onConfirm={() => issue(dialog.row)}
        title="Issue a certificate?"
        message={dialog?.row && (dialog.row.eligible
          ? `${dialog.row.name} attended ${dialog.row.percent}% and will receive their certificate by email and WhatsApp.`
          : `${dialog.row.name} ${dialog.row.attended ? `attended ${dialog.row.percent}%, below the ${bar}% bar` : "did not join the live session"}. Issuing anyway is recorded as your decision, and the certificate is sent to them.`)}
        confirmText="Issue certificate"
        variant="info"
        loading={busy}
      />
      <ConfirmModal
        show={dialog?.type === "rename"}
        onHide={() => setDialog(null)}
        onConfirm={() => issue(dialog.row, dialog.name)}
        title="Correct the name and reissue"
        message={(
          <FormField label="Name on the certificate" hint="The PDF is redrawn and sent again. Its certificate ID stays the same.">
            <Input
              value={dialog?.name || ""}
              maxLength={120}
              onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))}
            />
          </FormField>
        )}
        confirmText="Reissue"
        variant="info"
        loading={busy}
        confirmDisabled={!dialog?.name?.trim()}
      />
      <ConfirmModal
        show={dialog?.type === "revoke"}
        onHide={() => setDialog(null)}
        onConfirm={() => revoke(dialog.row, dialog.reason)}
        title="Revoke this certificate?"
        message={(
          <div className="tw-flex tw-flex-col tw-gap-3">
            <p className="tw-m-0">Its verification page will say it was revoked, and it can no longer be downloaded.</p>
            <FormField label="Reason (for your records)">
              <Textarea rows={2} maxLength={300} value={dialog?.reason || ""} onChange={(e) => setDialog((d) => ({ ...d, reason: e.target.value }))} />
            </FormField>
          </div>
        )}
        confirmText="Revoke"
        variant="danger"
        loading={busy}
      />
      <ConfirmModal
        show={dialog?.type === "release"}
        onHide={() => setDialog(null)}
        onConfirm={releaseAll}
        title={`Issue ${unissuedEligible} certificate${unissuedEligible === 1 ? "" : "s"}?`}
        message={`Everyone at ${bar}% or more receives their certificate by email and WhatsApp over the next few minutes. Later changes to the bar are applied automatically too.`}
        confirmText="Issue certificates"
        variant="success"
        loading={busy}
      />
    </div>
  );
};

export default AttendancePanel;
